import { Trans } from "@lingui/react/macro";

import { useAppConfig } from "~/hook/domain/useAppConfig.ts";
import { usePoke } from "~/hook/domain/usePoke.ts";
import { createWebDraggableSheet } from "~/ui/base/WebDraggableSheet.tsx";
import { WebPokeWithAudioSheet } from "~/ui/overlay/WebPokeWithAudioSheet.tsx";
import { WebPokeWithDrawingSheet } from "~/ui/overlay/WebPokeWithDrawingSheet.tsx";
import { WebPokeWithEmojiSheet } from "~/ui/overlay/WebPokeWithEmojiSheet.tsx";
import { WebPokeWithGeoLocationSheet } from "~/ui/overlay/WebPokeWithGeolocationSheet.tsx";
import { WebPokeWithPictureSheet } from "~/ui/overlay/WebPokeWithPictureSheet.tsx";
import { Layer, useStackedLayer } from "~/ui/provider/StackedLayerProvider.tsx";

const cx = {
  pokeButton:
    "h-12 rounded-2xl bg-zinc-100 dark:bg-zinc-800 px-4 text-start font-semibold duration-200 active:opacity-60 disabled:opacity-60 text-zinc-900 dark:text-zinc-100",
};

export const WebPokeSheet = createWebDraggableSheet<{
  targetUserEmail: string;
}>(({ close, context, titleId }) => {
  const { isMutating, trigger } = usePoke();
  const {
    isAudioPokeEnabled,
    isDrawingPokeEnabled,
    isEmojiPokeEnabled,
    isGeolocationPokeEnabled,
    isNormalPokeEnabled,
    isPicturePokeEnabled,
  } = useAppConfig();
  const overlay = useStackedLayer();

  const closeAndOpen = (Sheet: Layer<{ email: string }>) => {
    close();
    setTimeout(() => {
      overlay(Sheet, { email: context.targetUserEmail });
    }, 200);
  };

  const normalPoke = () => {
    void trigger({
      email: context.targetUserEmail,
      payload: { type: "normal" },
    }).finally(() => {
      close();
    });
  };

  return (
    <div data-testid="콕찌르기 모달">
      <h1
        className="text-lg font-semibold text-zinc-800 dark:text-zinc-100"
        id={titleId}
      >
        <Trans>콕! 찌르기 👉</Trans>
      </h1>
      <div className="flex flex-col gap-4 pt-6">
        {isAudioPokeEnabled && (
          <button
            className={cx.pokeButton}
            disabled={isMutating}
            onClick={() => {
              closeAndOpen(WebPokeWithAudioSheet);
            }}
          >
            <Trans>소리 찌르기 🎙️</Trans>
          </button>
        )}
        {isPicturePokeEnabled && (
          <button
            className={cx.pokeButton}
            disabled={isMutating}
            onClick={() => {
              closeAndOpen(WebPokeWithPictureSheet);
            }}
          >
            <Trans>사진 찌르기 📸</Trans>
          </button>
        )}
        {isDrawingPokeEnabled && (
          <button
            className={cx.pokeButton}
            disabled={isMutating}
            onClick={() => {
              closeAndOpen(WebPokeWithDrawingSheet);
            }}
          >
            <Trans>그림 찌르기 🎨</Trans>
          </button>
        )}
        {isGeolocationPokeEnabled && (
          <button
            className={cx.pokeButton}
            disabled={isMutating}
            onClick={() => {
              closeAndOpen(WebPokeWithGeoLocationSheet);
            }}
          >
            <Trans>내 위치 찌르기 📍</Trans>
          </button>
        )}
        {isEmojiPokeEnabled && (
          <button
            className={cx.pokeButton}
            disabled={isMutating}
            onClick={() => {
              closeAndOpen(WebPokeWithEmojiSheet);
            }}
          >
            <Trans>이모티콘 찌르기 😊</Trans>
          </button>
        )}
        {isNormalPokeEnabled && (
          <button
            className={cx.pokeButton}
            disabled={isMutating}
            onClick={normalPoke}
          >
            <Trans>바로 콕! 찌르기 👉</Trans>
          </button>
        )}
      </div>
    </div>
  );
});
